import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Map of form names to control paths to user-facing Spanish labels.
 *
 * Shape: Record<formName, Record<controlPath, label>>
 * Coverage: all FormControls that carry Validators.required in
 * formHotel, formTransfer, and formRentACar.
 *
 * For dynamic FormArrays (hotelNames, room, room.childsage), entries are listed
 * for indices 0-9 (hotel max 10, room max 7 with up to 4 children each = 28 indices).
 * For higher indices the same label pattern applies — the lookup in markFormInvalid
 * strips the index suffix when checking the map base.
 */
export const REQUIRED_FIELDS_LABELS: Record<string, Record<string, string>> = {
  hotel: {
    'file_id':                        'File (seleccioná o creá uno)',
    'client.client_id':               'Cliente (seleccioná o creá uno)',
    'destination':                     'Destino',
    'arrivalDepartureDate':           'Check-in / Check-out',
    'hotelNames[0].name':             'Nombre del hotel (1)',
    'hotelNames[1].name':             'Nombre del hotel (2)',
    'hotelNames[2].name':             'Nombre del hotel (3)',
    'hotelNames[3].name':             'Nombre del hotel (4)',
    'hotelNames[4].name':             'Nombre del hotel (5)',
    'hotelNames[5].name':             'Nombre del hotel (6)',
    'hotelNames[6].name':             'Nombre del hotel (7)',
    'hotelNames[7].name':             'Nombre del hotel (8)',
    'hotelNames[8].name':             'Nombre del hotel (9)',
    'hotelNames[9].name':             'Nombre del hotel (10)',
    'client.first_name':              'Nombre del cliente',
    'client.email':                   'Email del cliente',
    'client.phone_code':              'Código de país',
    'client.phone':                   'Teléfono del cliente',
    'requestType':                    'Tipo de solicitud',

    // Room FormArray — qtyAdults
    'room[0].qtyAdults':             'Habitación 1 - Adultos',
    'room[1].qtyAdults':             'Habitación 2 - Adultos',
    'room[2].qtyAdults':             'Habitación 3 - Adultos',
    'room[3].qtyAdults':             'Habitación 4 - Adultos',
    'room[4].qtyAdults':             'Habitación 5 - Adultos',
    'room[5].qtyAdults':             'Habitación 6 - Adultos',
    'room[6].qtyAdults':             'Habitación 7 - Adultos',

    // Room FormArray — childsage[].age
    'room[0].childsage[0].age':      'Habitación 1 - Edad del niño 1',
    'room[0].childsage[1].age':      'Habitación 1 - Edad del niño 2',
    'room[0].childsage[2].age':      'Habitación 1 - Edad del niño 3',
    'room[0].childsage[3].age':      'Habitación 1 - Edad del niño 4',
    'room[1].childsage[0].age':      'Habitación 2 - Edad del niño 1',
    'room[1].childsage[1].age':      'Habitación 2 - Edad del niño 2',
    'room[1].childsage[2].age':      'Habitación 2 - Edad del niño 3',
    'room[1].childsage[3].age':      'Habitación 2 - Edad del niño 4',
    'room[2].childsage[0].age':      'Habitación 3 - Edad del niño 1',
    'room[2].childsage[1].age':      'Habitación 3 - Edad del niño 2',
    'room[2].childsage[2].age':      'Habitación 3 - Edad del niño 3',
    'room[2].childsage[3].age':      'Habitación 3 - Edad del niño 4',
    'room[3].childsage[0].age':      'Habitación 4 - Edad del niño 1',
    'room[3].childsage[1].age':      'Habitación 4 - Edad del niño 2',
    'room[3].childsage[2].age':      'Habitación 4 - Edad del niño 3',
    'room[3].childsage[3].age':      'Habitación 4 - Edad del niño 4',
    'room[4].childsage[0].age':      'Habitación 5 - Edad del niño 1',
    'room[4].childsage[1].age':      'Habitación 5 - Edad del niño 2',
    'room[4].childsage[2].age':      'Habitación 5 - Edad del niño 3',
    'room[4].childsage[3].age':      'Habitación 5 - Edad del niño 4',
    'room[5].childsage[0].age':      'Habitación 6 - Edad del niño 1',
    'room[5].childsage[1].age':      'Habitación 6 - Edad del niño 2',
    'room[5].childsage[2].age':      'Habitación 6 - Edad del niño 3',
    'room[5].childsage[3].age':      'Habitación 6 - Edad del niño 4',
    'room[6].childsage[0].age':      'Habitación 7 - Edad del niño 1',
    'room[6].childsage[1].age':      'Habitación 7 - Edad del niño 2',
    'room[6].childsage[2].age':      'Habitación 7 - Edad del niño 3',
    'room[6].childsage[3].age':      'Habitación 7 - Edad del niño 4',
  },

  transfer: {
    'firstArrivalDate':              'Fecha de recogida',
    'firstTime':                      'Hora de recogida',
    'firstOrigin':                    'Origen',
    'firstDestination':               'Destino',
    'adults':                         'Adultos',
  },

  rentACar: {
    'origin':                         'Lugar de recogida',
    'pickupDate':                     'Fecha de retiro',
    'pickupTimeDay':                  'Horario de traslado (retiro)',
    'dropoffDate':                    'Fecha de devolución',
    'dropoffTimeDay':                 'Horario de traslado (devolución)',
    'adults':                         'Adultos',
  },

  experience: {
    'destination':                    'Destino',
    'checkIn':                        'Check-in',
    'checkOut':                       'Check-out',
    'type':                           'Tipo de experiencia',
    'fileId':                         'File',
    'clientId':                       'Cliente',
  },
};

/**
 * Specific Spanish error messages for each required control.
 * Used by the inline `<span class="field-error">` in the template
 * (NOT by the toast list — that uses the label).
 *
 * Falls back to the label when no message is provided.
 */
export const REQUIRED_FIELDS_MESSAGES: Record<string, Record<string, string>> = {
  hotel: {
    'file_id':                        'Seleccioná o creá un File.',
    'client.client_id':               'Seleccioná o creá un Cliente.',
    'destination':                    'Ingresá una ciudad, país o región.',
    'arrivalDepartureDate':           'Elegí fecha de check-in y check-out.',
    'hotelNames[0].name':             'Ingresá el nombre del hotel.',
    'hotelNames[1].name':             'Ingresá el nombre del hotel 2.',
    'hotelNames[2].name':             'Ingresá el nombre del hotel 3.',
    'hotelNames[3].name':             'Ingresá el nombre del hotel 4.',
    'hotelNames[4].name':             'Ingresá el nombre del hotel 5.',
    'hotelNames[5].name':             'Ingresá el nombre del hotel 6.',
    'hotelNames[6].name':             'Ingresá el nombre del hotel 7.',
    'hotelNames[7].name':             'Ingresá el nombre del hotel 8.',
    'hotelNames[8].name':             'Ingresá el nombre del hotel 9.',
    'hotelNames[9].name':             'Ingresá el nombre del hotel 10.',
    'client.first_name':              'Ingresá el nombre del cliente.',
    'client.email':                   'Ingresá un email válido.',
    'client.phone_code':              'Elegí el código de país.',
    'client.phone':                   'Ingresá el teléfono (mínimo 6 dígitos).',
    'requestType':                    'Elegí el tipo de solicitud.',
    'room[0].qtyAdults':              'Indicá al menos 1 adulto en la habitación 1.',
    'room[1].qtyAdults':              'Indicá al menos 1 adulto en la habitación 2.',
    'room[2].qtyAdults':              'Indicá al menos 1 adulto en la habitación 3.',
    'room[3].qtyAdults':              'Indicá al menos 1 adulto en la habitación 4.',
    'room[4].qtyAdults':              'Indicá al menos 1 adulto en la habitación 5.',
    'room[5].qtyAdults':              'Indicá al menos 1 adulto en la habitación 6.',
    'room[6].qtyAdults':              'Indicá al menos 1 adulto en la habitación 7.',
    'room[0].childsage[0].age':       'Indicá la edad del niño 1 (habitación 1).',
    'room[0].childsage[1].age':       'Indicá la edad del niño 2 (habitación 1).',
    'room[0].childsage[2].age':       'Indicá la edad del niño 3 (habitación 1).',
    'room[0].childsage[3].age':       'Indicá la edad del niño 4 (habitación 1).',
    'room[1].childsage[0].age':       'Indicá la edad del niño 1 (habitación 2).',
    'room[1].childsage[1].age':       'Indicá la edad del niño 2 (habitación 2).',
    'room[1].childsage[2].age':       'Indicá la edad del niño 3 (habitación 2).',
    'room[1].childsage[3].age':       'Indicá la edad del niño 4 (habitación 2).',
    'room[2].childsage[0].age':       'Indicá la edad del niño 1 (habitación 3).',
    'room[2].childsage[1].age':       'Indicá la edad del niño 2 (habitación 3).',
    'room[2].childsage[2].age':       'Indicá la edad del niño 3 (habitación 3).',
    'room[2].childsage[3].age':       'Indicá la edad del niño 4 (habitación 3).',
    'room[3].childsage[0].age':       'Indicá la edad del niño 1 (habitación 4).',
    'room[3].childsage[1].age':       'Indicá la edad del niño 2 (habitación 4).',
    'room[3].childsage[2].age':       'Indicá la edad del niño 3 (habitación 4).',
    'room[3].childsage[3].age':       'Indicá la edad del niño 4 (habitación 4).',
    'room[4].childsage[0].age':       'Indicá la edad del niño 1 (habitación 5).',
    'room[4].childsage[1].age':       'Indicá la edad del niño 2 (habitación 5).',
    'room[4].childsage[2].age':       'Indicá la edad del niño 3 (habitación 5).',
    'room[4].childsage[3].age':       'Indicá la edad del niño 4 (habitación 5).',
    'room[5].childsage[0].age':       'Indicá la edad del niño 1 (habitación 6).',
    'room[5].childsage[1].age':       'Indicá la edad del niño 2 (habitación 6).',
    'room[5].childsage[2].age':       'Indicá la edad del niño 3 (habitación 6).',
    'room[5].childsage[3].age':       'Indicá la edad del niño 4 (habitación 6).',
    'room[6].childsage[0].age':       'Indicá la edad del niño 1 (habitación 7).',
    'room[6].childsage[1].age':       'Indicá la edad del niño 2 (habitación 7).',
    'room[6].childsage[2].age':       'Indicá la edad del niño 3 (habitación 7).',
    'room[6].childsage[3].age':       'Indicá la edad del niño 4 (habitación 7).',
  },
  transfer: {
    'firstArrivalDate':               'Elegí la fecha de recogida.',
    'firstTime':                      'Elegí la hora de recogida.',
    'firstOrigin':                    'Ingresá el origen.',
    'firstDestination':               'Ingresá el destino.',
    'adults':                         'Indicá la cantidad de adultos.',
  },
  rentACar: {
    'origin':                         'Ingresá el lugar de recogida.',
    'pickupDate':                     'Elegí la fecha de retiro.',
    'pickupTimeDay':                  'Elegí al menos un horario de retiro.',
    'dropoffDate':                    'Elegí la fecha de devolución.',
    'dropoffTimeDay':                 'Elegí al menos un horario de devolución.',
    'adults':                         'Indicá la cantidad de adultos.',
  },

  experience: {
    'destination':                    'Seleccioná un destino.',
    'checkIn':                        'Seleccioná la fecha de check-in.',
    'checkOut':                       'Seleccioná la fecha de check-out.',
    'type':                           'Seleccioná el tipo de experiencia.',
    'fileId':                         'Seleccioná un file.',
    'clientId':                       'Seleccioná un cliente.',
    'dateRange':                      'La fecha de check-out debe ser posterior al check-in.',
  },
};

/**
 * Returns the label for a given control path within a specific form.
 * Falls back to 'Campo requerido' if the path is not found.
 */
export function getLabelForPath(formName: string, path: string): string {
  const formLabels = REQUIRED_FIELDS_LABELS[formName];
  if (!formLabels) return 'Campo requerido';
  return formLabels[path] ?? genericLabel(path);
}

/**
 * Returns a specific, contextual error message for a required control
 * (e.g. "Ingresá el nombre del hotel" instead of generic "Obligatorio").
 * Falls back to the label if no specific message is defined.
 */
export function getMessageForPath(formName: string, path: string): string {
  const formMessages = REQUIRED_FIELDS_MESSAGES[formName];
  if (formMessages) {
    const direct = formMessages[path];
    if (direct) return direct;
    // Try wildcard match: room[3].qtyAdults -> room[*].qtyAdults
    const normalized = path.replace(/\[\d+\]/g, '[*]');
    for (const key in formMessages) {
      const pattern = key.replace(/\[\d+\]/g, '[*]');
      if (pattern === normalized) {
        return formMessages[key];
      }
    }
  }
  // Fall back to the label (less specific but at least readable)
  return getLabelForPath(formName, path);
}

/**
 * Fallback label generator for paths not explicitly listed
 * (e.g., dynamic array indices beyond the enumerated range).
 */
function genericLabel(path: string): string {
  // Strip array index: "room[3].qtyAdults" -> try "room[*].qtyAdults"
  const normalized = path.replace(/\[\d+\]/g, '[*]');
  for (const formName in REQUIRED_FIELDS_LABELS) {
    const formLabels = REQUIRED_FIELDS_LABELS[formName];
    for (const key in formLabels) {
      const pattern = key.replace(/\[\d+\]/g, '[*]');
      if (pattern === normalized) {
        return formLabels[key];
      }
    }
  }
  return 'Campo requerido';
}

/**
 * Returns true if `path` (or its base array) is a known required path
 * within the given form.
 */
function isRequiredPath(formName: string, path: string): boolean {
  const formLabels = REQUIRED_FIELDS_LABELS[formName];
  if (!formLabels) return false;
  if (formLabels[path] !== undefined) return true;
  // Try stripping the last [index] segment to find the array base
  const baseMatch = path.match(/^(.+)\[\d+\]\.?.*$/);
  if (baseMatch) {
    const base = baseMatch[1];
    // Check both the exact base and the wildcard form
    if (formLabels[base] !== undefined) return true;
    const wildcardBase = base + '[*]';
    for (const key in formLabels) {
      if (key.replace(/\[\d+\]/g, '[*]') === wildcardBase) return true;
    }
  }
  return false;
}

/**
 * Recursively walks a FormGroup (including FormArrays inside it).
 * For every invalid control whose path matches a required path in the given form,
 * calls markAsTouched() so existing inline error conditions light up.
 *
 * Returns a de-duplicated list of { label, path } for the summary block,
 * in the order they were first encountered.
 */
export function markFormInvalid(
  group: FormGroup,
  formName: string
): { label: string; path: string }[] {
  const results: { label: string; path: string }[] = [];
  const seen = new Set<string>();

  function collect(control: AbstractControl, path: string): void {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        collect(control.get(key)!, path ? `${path}.${key}` : key);
      });
    } else if (control instanceof FormArray) {
      (control as FormArray).controls.forEach((c, i) => {
        collect(c, `${path}[${i}]`);
      });
    } else {
      if (control.invalid && isRequiredPath(formName, path)) {
        control.markAsTouched({ onlySelf: true });
        if (!seen.has(path)) {
          seen.add(path);
          results.push({ label: getLabelForPath(formName, path), path });
        }
      }
    }
  }

  collect(group, '');
  // Además de marcar los controles required individualmente, forzamos
  // markAllAsTouched() + updateValueAndValidity() sobre todo el FormGroup.
  // Eso garantiza que cualquier FormControl inválido (incluso los que no estén
  // en el map) muestre su mensaje inline cuando se ata a `*.touched`. Necesario
  // porque formHotel.reset() resetea `touched`, y controles como
  // `arrivalDepartureDate` (bindeados a ngxDaterangepickerMd) no se enteran
  // solos de que están vacíos. updateValueAndValidity fuerza a Angular a
  // re-evaluar validity (sin esto, controls "huérfanos" pueden quedar como
  // válidos aunque value sea null).
  group.markAllAsTouched();
  group.updateValueAndValidity();
  return results;
}

/**
 * Scrolls the first element inside `scope` that has attribute
 * data-form-invalid="{{ formName }}" into view.
 */
export function scrollToFirstInvalid(scope: HTMLElement, formName: string): void {
  const el = getFirstInvalidElement(scope, formName);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

/**
 * Returns the first element inside `scope` with attribute data-form-invalid="{{ formName }}",
 * or null if none found.
 */
export function getFirstInvalidElement(scope: HTMLElement, formName: string): HTMLElement | null {
  return scope.querySelector(`[data-form-invalid="${formName}"]`);
}
